import type { Lang } from '../i18n/strings.ts';

/**
 * Поиск адреса и подпись точки на карте через Nominatim (OpenStreetMap). Своего геокодера у
 * API нет, а в браузере нет системного, поэтому это единственный запрос приложения мимо
 * `api/endpoints.ts`. Публичный Nominatim принимает не больше запроса в секунду: экран ждёт
 * паузу во вводе и отменяет устаревший запрос.
 */
const NOMINATIM = 'https://nominatim.openstreetmap.org';

/** Белград: центр и размах, которого хватает на город с пригородами. */
export const BELGRADE = { lat: 44.8125, lng: 20.4612, latSpan: 0.35, lngSpan: 0.45 };

export type AddressSuggestion = { id: string; title: string; subtitle: string; lat: number; lng: number };

type NominatimPlace = {
  place_id?: number;
  lat?: string;
  lon?: string;
  name?: string;
  display_name?: string;
  address?: {
    road?: string;
    house_number?: string;
    suburb?: string;
    town?: string;
    village?: string;
    city?: string;
  };
};

/** Сербские названия латиницей при любом языке, кроме английского: так адрес прочтёт курьер. */
function acceptLanguage(lang: Lang): string {
  return lang === 'en' ? 'en' : 'sr-Latn';
}

async function ask<T>(path: string, query: Record<string, string>, lang: Lang, signal: AbortSignal | undefined): Promise<T | null> {
  const params = new URLSearchParams({ format: 'jsonv2', addressdetails: '1', 'accept-language': acceptLanguage(lang), ...query });
  try {
    const response = await fetch(`${NOMINATIM}${path}?${params.toString()}`, signal ? { signal } : {});
    return response.ok ? ((await response.json()) as T) : null;
  } catch {
    return null;
  }
}

/** «Njegoševa 5»; без улицы — название места или первая часть полного адреса. */
function streetOf(place: NominatimPlace): string | null {
  const street = [place.address?.road, place.address?.house_number].filter(Boolean).join(' ');
  return street || place.name || place.display_name?.split(',')[0]?.trim() || null;
}

/** Часть города: по ней различаются одноимённые улицы. */
function localityOf(place: NominatimPlace): string | null {
  const address = place.address;
  return address?.town ?? address?.village ?? address?.suburb ?? address?.city ?? null;
}

/** Подсказки по строке поиска. Только Белград: иначе «Kneza Miloša» находится в трёх странах. */
export async function searchAddress(text: string, lang: Lang, signal?: AbortSignal): Promise<AddressSuggestion[]> {
  const left = BELGRADE.lng - BELGRADE.lngSpan / 2;
  const right = BELGRADE.lng + BELGRADE.lngSpan / 2;
  const top = BELGRADE.lat + BELGRADE.latSpan / 2;
  const bottom = BELGRADE.lat - BELGRADE.latSpan / 2;
  const places = await ask<NominatimPlace[]>(
    '/search',
    { q: text, limit: '5', countrycodes: 'rs', viewbox: `${left},${top},${right},${bottom}`, bounded: '1' },
    lang,
    signal,
  );

  const result: AddressSuggestion[] = [];
  for (const place of places ?? []) {
    const title = streetOf(place);
    const lat = Number(place.lat);
    const lng = Number(place.lon);
    if (!title || !Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    result.push({ id: String(place.place_id ?? `${lat},${lng}`), title, subtitle: localityOf(place) ?? '', lat, lng });
  }
  return result;
}

/** Название точки после сдвига карты: улица и дом, если карта их знает. */
export async function describePoint(lat: number, lng: number, lang: Lang, signal?: AbortSignal): Promise<string | null> {
  const place = await ask<NominatimPlace>('/reverse', { lat: String(lat), lon: String(lng), zoom: '18' }, lang, signal);
  if (!place) return null;
  const parts = [streetOf(place), localityOf(place)].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(', ') : null;
}
